/**
 * Approvals (/dashboard/admin): the Chicago Politics staff page. Staff = members of the "dashboard" platform team whose
 * email is on the staff domain (Worker variable STAFF_EMAIL_DOMAIN, read only by worker/src/dashboard-staff.js and
 * passed to the page in snapshot:admin). Shared by the Worker and the page so both check the same rule.
 */
import type { AnalysisStatus, ApiAuditEntry, ApprovalDecision, ApprovalRequest, ForecastStatus, MayorVideoStatus } from "./types";

/** `email` is an address on `domain` (exactly; not a subdomain, not a look-alike). No domain: nobody is staff. */
export function isStaffEmail(email: string | null | undefined, domain: string | null | undefined): boolean {
  const d = String(domain ?? "").trim().toLowerCase().replace(/^@/, "");
  return !!d && /^[^\s@]+@[^\s@]+$/.test(String(email ?? "").trim()) && String(email).trim().toLowerCase().endsWith(`@${d}`);
}

/** The organization column: the team's name, or the ward for offices and residents. */
export function requestTitle(r: Pick<ApprovalRequest, "kind" | "orgName" | "ward">): string {
  if (r.kind === "office") return r.orgName ?? (r.ward ? `Ward ${r.ward} office` : "Alderperson’s office");
  if (r.kind === "advocacy") return r.orgName ?? "Lobbyist / advocacy";
  return r.ward ? `Ward ${r.ward}` : "Resident";
}

export const GROUP_LABEL: Record<ApprovalRequest["kind"], string> = { office: "Alderperson’s office", advocacy: "Lobbyist / advocacy", resident: "Resident" };

// ---------- Organizations (/dashboard/admin/orgs)

export const TEAM_GROUP_LABEL: Record<"office" | "advocacy" | "platform", string> = { office: GROUP_LABEL.office, advocacy: GROUP_LABEL.advocacy, platform: "Chicago Politics" };

/** A feature's value as shown: flags On / Off; limits the number, or None. */
export function valueText(kind: "flag" | "limit", v: boolean | number | null): string {
  if (kind === "flag") return v ? "On" : "Off";
  return typeof v === "number" ? v.toLocaleString("en-US") : "None";
}

/** An audit side: the override ({ enabled, limit, expiresAt }) or null = the group default. */
export function overrideText(kind: "flag" | "limit", o: { enabled: boolean | null; limit: number | null; expiresAt: string | null } | null): string {
  if (!o) return "Default";
  const v = kind === "flag" ? valueText("flag", !!o.enabled) : o.enabled === false ? "None" : valueText("limit", o.limit);
  return o.expiresAt ? `${v} until ${new Date(o.expiresAt).toLocaleDateString("en-US", { dateStyle: "medium" })}` : v;
}

/** A date input's value (YYYY-MM-DD) → the end of that day (local time), as ISO; anything else → null. */
export function expiryIso(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const t = new Date(`${date}T23:59:59`);
  return Number.isNaN(t.getTime()) ? null : t.toISOString();
}
export const dateInput = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Oldest request first (they've waited longest); decisions newest first. */
export const byOldest = (a: ApprovalRequest, b: ApprovalRequest) => String(a.requestedAt ?? "").localeCompare(String(b.requestedAt ?? ""));
export const byNewest = (a: ApprovalDecision, b: ApprovalDecision) => b.decidedAt.localeCompare(a.decidedAt);

// ---------- API audit (Organizations; GET /api/dashboard/admin/api-audit)

/** Words from Settings → API keys. */
export const API_AUDIT_ACTION: Record<ApiAuditEntry["action"], string> = {
  "key.create": "Create key", "key.update": "Edit", "key.revoke": "Revoke", "key.auto_revoke": "Revoke",
  "oauth.grant": "Connect", "oauth.revoke": "Disconnect",
};
const FIELD_LABEL: Record<string, string> = { name: "Name", expiresAt: "Expires", ipAllow: "Allowed IPs", scopes: "Scopes" };
const SECRET = /hash|token|secret/i;

const sideText = (field: string, v: unknown): string => {
  if (v == null || v === "") return field === "ipAllow" ? "Any" : field === "expiresAt" ? "Never" : "";
  if (Array.isArray(v)) return v.join(" ");
  if (field === "expiresAt" && typeof v === "string") return new Date(v).toLocaleDateString("en-US", { dateStyle: "medium" });
  return String(v);
};

/** One API audit row as shown: action, the key or app, whose it is, and what changed. Never a hash or token field. */
export function apiAuditRow(e: ApiAuditEntry): { action: string; name: string; detail: string | null; owner: string | null; change: string } {
  const fields = Object.keys({ ...(e.before ?? {}), ...(e.after ?? {}) }).filter((k) => FIELD_LABEL[k] && !SECRET.test(k));
  const change = e.action === "key.update"
    ? fields.map((k) => `${FIELD_LABEL[k]}: ${sideText(k, e.before?.[k])} → ${sideText(k, e.after?.[k])}`).join(" · ")
    : Array.isArray(e.after?.scopes) ? (e.after!.scopes as unknown[]).join(" ") : "";
  return {
    action: API_AUDIT_ACTION[e.action] ?? e.action,
    name: e.key ? e.key.name || e.key.prefix || e.key.id : e.app ? e.app.name || e.app.id : "",
    detail: e.key?.prefix ? `${e.key.prefix}…` : null,
    owner: e.user ? e.user.email || e.user.name : null,
    change,
  };
}

// ---------- Analysis (/dashboard/admin/analysis; GET /api/dashboard/admin/analysis)

const pct = (n: number, of: number) => (of > 0 ? Math.min(100, Math.max(0, Math.round((n / of) * 100))) : 0);
/** USD as shown: $0.42, $1,250.00; null → "—". */
export const usd = (n: number | null | undefined) => (typeof n === "number" && Number.isFinite(n) ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

export interface AnalysisPanel {
  on: boolean;
  error: string | null;
  progress: { done: number; total: number; pct: number };
  counts: { remaining: number; running: number; failed: number; skipped: number };
  spend: { today: number; budget: number; pct: number; left: number; total: number };
  average: number | null;
  errors: { key: string; meeting: string; when: string | null; error: string }[];
  skipped: { key: string; meeting: string; date: string | null; reason: string; error: string | null }[];
  /** All analysis paused (billing/auth): the error, since when, the next check, whether staff were emailed. */
  paused: { title: string; error: string; since: string; nextCheck: string; emailed: string | null } | null;
  /** A provider that is out while another one carries on (Claude out → Workers AI). */
  outages: { key: string; title: string; error: string; since: string; nextCheck: string; emailed: string | null }[];
  /** The model in use now and the setting; successful runs by model. */
  model: { using: string; setting: string };
  models: { key: string; model: string; runs: number; today: number; usd: number }[];
  /** Recent meetings still without a summary, with their next try. */
  retrying: { key: string; meeting: string; date: string | null; attempts: number; next: string | null; state: string; error: string | null }[];
  missingSummary: number;
  /** The Mayor's YouTube collection: counts by status, the last run. */
  videos: { rows: { key: string; label: string; count: number }[]; total: number; ok: number; pct: number; lastRun: string | null; stopped: string | null; cookies: boolean | null } | null;
}

const VIDEO_STATUS = [["ok", "Transcribed"], ["none", "No transcript"], ["new", "Not fetched yet"], ["waiting", "Waiting to retry"], ["error", "Errors"]] as const;
const STOPPED: Record<string, string> = { refused: "YouTube refused the runner", time: "Time budget spent" };
export function videosPanel(v: MayorVideoStatus | null | undefined): AnalysisPanel["videos"] {
  if (!v?.counts) return null;
  const ok = v.counts.ok ?? 0, total = v.total ?? 0;
  return {
    rows: VIDEO_STATUS.map(([key, label]) => ({ key, label, count: v.counts[key] ?? 0 })),
    total, ok, pct: pct(ok, total),
    lastRun: v.lastRun?.at ?? null,
    stopped: v.lastRun?.stopped ? STOPPED[v.lastRun.stopped] ?? v.lastRun.stopped : null,
    cookies: typeof v.lastRun?.cookies === "boolean" ? v.lastRun.cookies : null,
  };
}

/**
 * The sweep status → the panel. Progress counts only what the sweep has to do: total = done + remaining + skipped
 * (recordings already analyzed in full before the run log are left out). Spend today is everything the analysis spent
 * today (the budget counts every analysis), against ANALYSIS_DAILY_BUDGET_USD.
 */
const PROVIDER_LABEL = { claude: "Claude", "workers-ai": "Workers AI (GLM 5.3)" } as const;

export function analysisPanel(s: AnalysisStatus): AnalysisPanel {
  const n = (v: number | undefined) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
  const skipped = s.skipped ?? [];
  const done = n(s.done), total = done + n(s.remaining) + skipped.length;
  const today = n(s.spend?.todayUsd), budget = n(s.dailyBudgetUsd);
  const meeting = (m: { vimeoId: string; meetingId: string | null; body?: string | null }) => m.body || m.meetingId || m.vimeoId;
  return {
    on: !!s.on,
    error: s.error ?? null,
    progress: { done, total, pct: pct(done, total) },
    counts: { remaining: n(s.remaining), running: n(s.running), failed: n(s.failed), skipped: skipped.length },
    spend: { today, budget, pct: pct(today, budget), left: s.spend ? n(s.spend.budgetLeftTodayUsd) : Math.max(0, budget - today), total: n(s.spend?.totalUsd) },
    average: typeof s.averageRunUsd === "number" ? s.averageRunUsd : null,
    errors: (s.lastErrors ?? []).slice(0, 10).map((e, i) => ({ key: `${e.vimeoId}:${e.finishedAt ?? e.startedAt ?? i}`, meeting: e.meetingId || e.vimeoId, when: e.finishedAt ?? e.startedAt ?? null, error: e.error })),
    skipped: skipped.map((m) => ({ key: m.vimeoId, meeting: meeting(m), date: m.date || null, reason: m.reason === "failed" ? "Failed" : m.reason, error: m.error ?? null })),
    paused: s.paused ? {
      title: s.paused.provider === "workers-ai" ? "Paused: Workers AI and Claude unavailable"
        : s.paused.kind === "billing" ? "Paused: Anthropic credit balance too low" : "Paused: Claude refused our credentials",
      error: s.paused.error, since: s.paused.since, nextCheck: s.paused.until, emailed: s.paused.notifiedAt,
    } : null,
    outages: s.paused ? [] : (s.outages ?? []).map((o) => ({
      key: o.provider, error: o.error, since: o.since, nextCheck: o.until, emailed: o.notifiedAt,
      title: o.provider === "claude" ? (o.kind === "billing" ? "Claude unavailable: Anthropic credit balance too low" : "Claude unavailable: credentials refused") : "Workers AI unavailable",
    })),
    model: { using: s.using ? PROVIDER_LABEL[s.using] : s.paused ? "None" : "—", setting: s.provider === "claude" ? "Claude only" : s.provider === "workers-ai" ? "Workers AI only" : "Claude, then Workers AI" },
    models: (s.providers ?? []).map((m) => ({ key: `${m.provider}:${m.model ?? ""}`, model: m.model || PROVIDER_LABEL[m.provider], runs: n(m.runs), today: n(m.today), usd: n(m.usd) })),
    retrying: (s.retrying ?? []).map((m) => ({
      key: m.vimeoId, meeting: meeting(m), date: m.date || null, attempts: n(m.attempts), error: m.error ?? null,
      next: m.state === "waiting" ? m.retryAt : null,
      state: s.paused ? "Paused" : m.state === "running" ? "Running" : m.state === "waiting" ? "Waiting" : m.state === "fresh" ? "Starting" : "Next",
    })),
    missingSummary: n(s.missingSummary),
    videos: videosPanel(s.mayorVideos),
  };
}

/** The Forecast model card (Admin → Analysis): plain rows from the forecast status (worker/src/forecast-status.js). */
export interface ForecastPanel {
  active: string;                       // "v2026-10-13.1" | "Prior"
  trainedAt: string | null;
  latest: { version: string; trainedAt: string; promoted: boolean; reason: string } | null;
  /** Holdout log loss and Brier of the latest candidate, the prior and the active model on the same rows ("—" when absent). */
  scores: { label: string; candidate: string; prior: string; active: string }[];
  frozen: { forecasts: number; matters: number; open: number; lastFrozenAt: string | null };
  prospective: { called: string; brier: string; logloss: string; rollCalls: number } | null;
  nextCouncil: { date: string; docket: number } | null;
}

const score = (x: number | null | undefined) => (typeof x === "number" && Number.isFinite(x) ? x.toFixed(3) : "—");

export function forecastPanel(s: ForecastStatus | null | undefined): ForecastPanel | null {
  if (!s) return null;
  const l = s.latest, p = s.prospective;
  return {
    active: s.active?.version ?? "Prior",
    trainedAt: s.active?.trainedAt ?? null,
    latest: l ? { version: l.version, trainedAt: l.trainedAt, promoted: l.promoted, reason: l.reason } : null,
    scores: l ? [
      { label: "Log loss", candidate: score(l.holdout?.logloss), prior: score(l.prior?.logloss), active: score(l.vsActive?.logloss) },
      { label: "Brier", candidate: score(l.holdout?.brier), prior: score(l.prior?.brier), active: score(l.vsActive?.brier) },
    ] : [],
    frozen: { forecasts: s.frozen?.rows ?? 0, matters: s.frozen?.matters ?? 0, open: s.frozen?.open ?? 0, lastFrozenAt: s.frozen?.lastFrozenAt ?? null },
    prospective: p && p.memberVotes ? { called: `${p.called.toLocaleString("en-US")} of ${p.memberVotes.toLocaleString("en-US")}`, brier: score(p.brier), logloss: score(p.logloss), rollCalls: p.rollCalls } : null,
    nextCouncil: s.nextCouncil ?? null,
  };
}
